// src/context/CasinoEnabledContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSportToggle } from '../store/slices/sportsSlice';

const CASINO_ETID = 12;
const POLL_MS = 30000; // picks up admin changes without a manual refresh

const CasinoEnabledContext = createContext({ casinoEnabled: false, loading: true });

// Home.js reads `enabled`, so that field is checked first
const isOn = (item) => {
  if (!item) return false;
  const v = item.enabled ?? item.isEnabled ?? item.isActive ?? item.active ?? item.status;
  return (
    v === true ||
    v === 1 ||
    v === '1' ||
    v === 'true' ||
    (typeof v === 'string' && ['active', 'on', 'enabled'].includes(v.toLowerCase()))
  );
};

export const CasinoEnabledProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s) => s.auth.authenticated);
  const toggleData = useSelector((s) => s.sports.getSportToggleData);
  const [status, setStatus] = useState({ loaded: false, failed: false });

  // Fetch on app start, refetch on login/logout, and poll
  useEffect(() => {
    let cancelled = false;

    const load = (initial) => {
      if (initial) setStatus({ loaded: false, failed: false });
      dispatch(getSportToggle()).then((action) => {
        if (cancelled) return;
        setStatus({ loaded: true, failed: getSportToggle.rejected.match(action) });
      });
    };

    load(true);
    const id = setInterval(() => load(false), POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dispatch, isAuthenticated]);

  const value = useMemo(() => {
    const list = Array.isArray(toggleData) ? toggleData : toggleData?.sports || [];
    const item = list.find((i) => Number(i.etid) === CASINO_ETID);
    return {
      // failed fetch (e.g. 401 for guests) => Casino OFF, same result on logout and refresh
      casinoEnabled: !status.failed && isOn(item),
      loading: !status.loaded,
      fetchFailed: status.failed,
    };
  }, [toggleData, status]);

  return (
    <CasinoEnabledContext.Provider value={value}>
      {children}
    </CasinoEnabledContext.Provider>
  );
};

export const useCasinoEnabled = () => useContext(CasinoEnabledContext);

export default CasinoEnabledContext;